import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Image, Plus, RefreshCw, Loader2, Star, Users, 
  Upload, Edit3, X, Eye, Briefcase 
} from 'lucide-react';

const API = window.location.origin;

// Helper to get image URL - handles Google Drive URLs via proxy
const getImageUrl = (url) => {
  if (!url) return '';
  // If it's already a full URL (Drive, external)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Convert Google Drive URLs to use our proxy (avoids CORS issues)
    // Pattern 1: https://drive.google.com/file/d/FILE_ID/view
    const driveViewMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)\/view/);
    if (driveViewMatch) {
      return `${API}/api/drive-proxy/${driveViewMatch[1]}`;
    }
    // Pattern 2: https://drive.google.com/uc?export=view&id=FILE_ID
    const driveUcMatch = url.match(/drive\.google\.com\/uc\?export=view&id=([^&]+)/);
    if (driveUcMatch) {
      return `${API}/api/drive-proxy/${driveUcMatch[1]}`;
    }
    // Pattern 3: https://drive.google.com/uc?id=FILE_ID&export=view
    const driveUcMatch2 = url.match(/drive\.google\.com\/uc\?id=([^&]+)/);
    if (driveUcMatch2) {
      return `${API}/api/drive-proxy/${driveUcMatch2[1]}`;
    }
    // Pattern 4: https://lh3.googleusercontent.com/d/FILE_ID=s1600
    const lh3Match = url.match(/lh3\.googleusercontent\.com\/d\/([^=]+)/);
    if (lh3Match) {
      return `${API}/api/drive-proxy/${lh3Match[1]}`;
    }
    return url;
  }
  // Otherwise, it's a local URL, prepend backend URL
  return `${API}${url.startsWith('/') ? '' : '/'}${url}`;
};

export const PortfolioTab = ({ 
  token, 
  customers,
  onViewCustomer,
  onGoToCustomers,
  onPhotoSelect 
}) => {
  const [portfolioPhotos, setPortfolioPhotos] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [isUploadingPortfolio, setIsUploadingPortfolio] = useState(false);
  const [portfolioCategoryFilter, setPortfolioCategoryFilter] = useState('all');
  const portfolioFileInputRef = useRef(null);
  const fetchedRef = useRef(false);

  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  // Fetch portfolio photos - use customers prop, only fetch standalone photos
  const fetchPortfolioPhotos = async () => {
    if (!token) { setPortfolioLoading(false); return; }
    setPortfolioLoading(true);
    
    try {
      // Get standalone portfolio photos
      let standalonePhotos = [];
      try {
        const res = await fetch(`${API}/api/admin/portfolio-photos`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          standalonePhotos = (data || []).map(p => ({
            ...p,
            customerName: p.customer_name || 'Direct Upload',
            isStandalone: true
          }));
        }
      } catch (err) { /* non-fatal */ }
      
      // Get featured photos from customers prop (no extra API call)
      let featuredPhotos = [];
      if (customers?.length > 0) {
        customers.forEach(customer => {
          customer.jobs?.forEach(job => {
            if (job.featured_in_portfolio && job.photos?.length > 0) {
              job.photos.forEach(photo => {
                const photoData = typeof photo === 'string'
                  ? { id: `${customer.id}_${Math.random()}`, url: photo }
                  : photo;
                featuredPhotos.push({
                  ...photoData,
                  customerName: customer.name,
                  customerId: customer.id,
                  jobDescription: job.description,
                  jobDate: job.date
                });
              });
            }
          });
        });
      }
      
      setPortfolioPhotos([...featuredPhotos, ...standalonePhotos]);
    } catch (error) {
      console.error("Error fetching portfolio:", error);
    } finally {
      setPortfolioLoading(false);
    }
  };

  useEffect(() => {
    if (token && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchPortfolioPhotos();
    }
    return () => { fetchedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Handle file upload
  const handlePortfolioFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setIsUploadingPortfolio(true);
    let successCount = 0;
    const headers = getHeaders();
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'portfolio');
        
        const uploadRes = await fetch(`${API}/api/files/upload`, {
          method: 'POST',
          headers: { Authorization: headers.Authorization },
          body: formData
        });
        if (!uploadRes.ok) throw new Error(`Upload HTTP ${uploadRes.status}`);
        const uploadData = await uploadRes.json();
        
        // Then add to portfolio with the URL
        const portfolioRes = await fetch(`${API}/api/admin/portfolio-photos`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            url: uploadData.url,
            caption: '',
            photo_type: 'general',
            category: 'general'
          })
        });
        if (!portfolioRes.ok) throw new Error(`Portfolio HTTP ${portfolioRes.status}`);
        
        successCount++;
      } catch (error) {
        console.error("Failed to upload:", file.name, error.message);
        toast.error(`Failed: ${file.name}`);
      }
    }
    
    setIsUploadingPortfolio(false);
    if (successCount > 0) {
      toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} uploaded!`);
      fetchedRef.current = false;
      fetchPortfolioPhotos();
    }
    e.target.value = '';
  };

  // Remove from portfolio
  const handleRemoveFromPortfolio = async (photo) => {
    const headers = getHeaders();
    try {
      if (photo.isStandalone) {
        await fetch(`${API}/api/admin/portfolio-photos/${photo.id}`, {
          method: 'DELETE',
          headers
        });
      } else {
        // Unfeatured from job
        await fetch(
          `${API}/api/admin/customers/${photo.customerId}/jobs/${photo.jobIndex}/photos/${photo.photoIndex}`,
          {
            method: 'PATCH',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ featured_in_portfolio: false })
          }
        );
      }
      toast.success("Removed from portfolio");
      fetchedRef.current = false;
      fetchPortfolioPhotos();
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <Image className="h-5 w-5" />
              Lightbox Organisation
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Manage photos that appear in your landing page portfolio
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => portfolioFileInputRef.current?.click()} className="gap-2 bg-accent hover:bg-accent/90">
              <Plus className="h-4 w-4" />
              Add Photo
            </Button>
            <Button onClick={fetchPortfolioPhotos} variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {portfolioLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : portfolioPhotos.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-lg">
            <Image className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-medium mb-2">No Photos in Portfolio</h3>
            <p className="text-muted-foreground mb-4">
              Click &quot;Add Photo&quot; above to add photos directly, or go to Customers to feature job photos
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => portfolioFileInputRef.current?.click()} className="bg-accent hover:bg-accent/90">
                <Plus className="h-4 w-4 mr-2" />
                Add Photo
              </Button>
              <Button onClick={onGoToCustomers} variant="outline">
                <Users className="h-4 w-4 mr-2" />
                Go to Customers
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Upload Area */}
            <div 
              className="border-2 border-dashed border-primary/30 rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => portfolioFileInputRef.current?.click()}
            >
              <input
                ref={portfolioFileInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                className="hidden"
                onChange={handlePortfolioFileUpload}
              />
              {isUploadingPortfolio ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-6 w-6 text-primary animate-spin" />
                  <span>Uploading...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <Upload className="h-6 w-6 text-primary/60" />
                  <span className="text-muted-foreground">Click to upload photos directly to your portfolio</span>
                </div>
              )}
            </div>

            {/* Stats and Category Filter */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-accent/5 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="bg-accent/20 p-3 rounded-lg">
                  <Star className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{portfolioPhotos.length}</p>
                  <p className="text-sm text-muted-foreground">Photos in your portfolio lightbox</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Filter by Category:</Label>
                <Select value={portfolioCategoryFilter} onValueChange={setPortfolioCategoryFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="carpentry">Carpentry</SelectItem>
                    <SelectItem value="general-repairs">General Repairs</SelectItem>
                    <SelectItem value="furniture-builds">Furniture Builds</SelectItem>
                    <SelectItem value="garden-storage">Garden Storage</SelectItem>
                    <SelectItem value="garden-buildings">Garden Buildings</SelectItem>
                    <SelectItem value="fencing-gates">Fencing & Gates</SelectItem>
                    <SelectItem value="cnc-factory">CNC/Factory Work</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Photo Grid */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <Label className="text-base">
                  Portfolio Photos 
                  {portfolioCategoryFilter !== 'all' && (
                    <Badge variant="secondary" className="ml-2 capitalize">{portfolioCategoryFilter}</Badge>
                  )}
                </Label>
                <p className="text-xs text-muted-foreground">Click edit to modify, X to remove</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {portfolioPhotos
                  .filter(photo => portfolioCategoryFilter === 'all' || (photo.category || 'general') === portfolioCategoryFilter)
                  .map((photo, index) => (
                  <div 
                    key={photo.id || index}
                    className="group relative rounded-lg overflow-hidden border-2 border-transparent hover:border-primary/50 transition-all"
                    data-testid={`portfolio-photo-${index}`}
                  >
                    {/* Remove Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRemoveFromPortfolio(photo); }}
                      className="absolute top-2 right-2 z-20 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full shadow-lg transition-colors"
                      title="Remove from portfolio"
                    >
                      <X className="h-3 w-3" />
                    </button>

                    {/* Edit Button */}
                    <button
                      onClick={() => onPhotoSelect && onPhotoSelect(photo)}
                      className="absolute top-2 left-2 z-20 bg-primary hover:bg-primary/80 text-white p-1.5 rounded-full shadow-lg transition-colors"
                      title="Edit photo"
                    >
                      <Edit3 className="h-3 w-3" />
                    </button>

                    {/* Photo Type Badge */}
                    {photo.photo_type && photo.photo_type !== 'general' && (
                      <div className={`absolute bottom-8 left-2 z-10 px-2 py-0.5 rounded text-xs font-bold pointer-events-none ${photo.photo_type === 'before' ? 'bg-orange-500 text-white' : 'bg-green-500 text-white'}`}>
                        {photo.photo_type.toUpperCase()}
                      </div>
                    )}

                    {/* Category Badge */}
                    {photo.category && photo.category !== 'general' && (
                      <div className="absolute top-10 left-2 z-10 px-2 py-0.5 rounded text-xs font-medium pointer-events-none bg-primary/80 text-white capitalize">
                        {photo.category}
                      </div>
                    )}

                    {/* Order Number */}
                    <div className="absolute bottom-2 right-2 z-10 bg-black/60 text-white text-xs px-2 py-1 rounded pointer-events-none">
                      #{index + 1}
                    </div>

                    {/* Image */}
                    <div className="aspect-square cursor-pointer" onClick={() => onPhotoSelect && onPhotoSelect(photo)}>
                      <img 
                        src={getImageUrl(photo.url)} 
                        alt={photo.caption || `Portfolio photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Caption */}
                    {photo.caption && (
                      <div className="p-2 bg-muted text-xs truncate">{photo.caption}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Grouped by Category */}
            <div className="border-t pt-6">
              <Label className="text-base mb-4 block">Photos by Category</Label>
              <div className="space-y-4">
                {Object.entries(
                  portfolioPhotos.reduce((acc, photo) => {
                    const cat = photo.category || 'general';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(photo);
                    return acc;
                  }, {})
                ).map(([category, photos]) => (
                  <div key={category} className="bg-accent/5 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-accent" />
                        <span className="font-medium capitalize">{category}</span>
                        <Badge variant="secondary">{photos.length} photo{photos.length > 1 ? 's' : ''}</Badge>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setPortfolioCategoryFilter(category)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Filter
                      </Button>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {photos.slice(0, 6).map((photo, idx) => (
                        <div 
                          key={photo.id || idx}
                          className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden group"
                        >
                          <img 
                            src={getImageUrl(photo.url)} 
                            alt="" 
                            className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => onPhotoSelect && onPhotoSelect(photo)}
                          />
                        </div>
                      ))}
                      {photos.length > 6 && (
                        <div 
                          className="flex-shrink-0 w-20 h-20 rounded-lg bg-muted flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors"
                          onClick={() => setPortfolioCategoryFilter(category)}
                        >
                          <span className="text-sm text-muted-foreground font-medium">+{photos.length - 6}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Grouped by Customer */}
            <div className="border-t pt-6">
              <Label className="text-base mb-4 block">Photos by Customer</Label>
              <div className="space-y-4">
                {Object.entries(
                  portfolioPhotos.reduce((acc, photo) => {
                    if (!acc[photo.customerName]) acc[photo.customerName] = [];
                    acc[photo.customerName].push(photo);
                    return acc;
                  }, {})
                ).map(([customerName, photos]) => (
                  <div key={customerName} className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{customerName}</span>
                        <Badge variant="secondary">{photos.length} photo{photos.length > 1 ? 's' : ''}</Badge>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          const customer = customers?.find(c => c.name === customerName);
                          if (customer && onViewCustomer) {
                            onViewCustomer(customer);
                          }
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Customer
                      </Button>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {photos.map((photo, idx) => (
                        <div 
                          key={photo.id || idx}
                          className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden group"
                        >
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRemoveFromPortfolio(photo); }}
                            className="absolute top-1 right-1 z-10 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                          <img 
                            src={getImageUrl(photo.url)} 
                            alt="" 
                            className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => onPhotoSelect && onPhotoSelect(photo)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PortfolioTab;
